import { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useCustomerAuth } from '@/hooks/useAuth';
import { useConfig, useTheme, EnvironmentBanner, useBannerShown } from '@connor-music/shared';
import {
  Home,
  UploadCloud,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Monitor,
  User,
  PanelLeftClose,
  PanelLeft,
} from 'lucide-react';
import { clsx } from 'clsx';

const navItems = [
  { path: '/', icon: Home, label: 'Dashboard', exact: true },
  { path: '/uploads', icon: UploadCloud, label: 'Upload History' },
];

export function CustomerLayout() {
  const { customer, logout } = useCustomerAuth();
  const { config } = useConfig();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const logoUrl = resolvedTheme === 'dark' ? config.logo_dark_url : config.logo_light_url;
  const logoMarkUrl = resolvedTheme === 'dark' ? config.logo_mark_dark_url : config.logo_mark_light_url;
  const portalName = config.portal_name || 'Connor Music Audit';

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ] as const;

  const bannerShown = useBannerShown();
  const mainPadding = sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64';

  return (
    <div className={clsx('min-h-screen bg-gray-50 dark:bg-gray-900', bannerShown && 'pt-7')}>
      <EnvironmentBanner />

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600/75 dark:bg-gray-900/75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed left-0 z-50 bg-white dark:bg-gray-800 shadow-lg transform transition-all duration-200 ease-in-out lg:translate-x-0',
          bannerShown ? 'top-7 bottom-0' : 'inset-y-0',
          sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full',
          sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={clsx(
            'flex items-center h-16 border-b border-gray-200 dark:border-gray-700',
            sidebarCollapsed ? 'justify-center px-2' : 'justify-between px-4'
          )}>
            {sidebarCollapsed ? (
              <NavLink to="/" className="flex items-center justify-center">
                {logoMarkUrl ? (
                  <img src={logoMarkUrl} alt="" className="h-8 w-8" />
                ) : (
                  <span className="font-bold text-gray-900 dark:text-white text-lg">C</span>
                )}
              </NavLink>
            ) : (
              <>
                <NavLink to="/" className="flex items-center space-x-2">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={portalName}
                      style={{ width: config.logo_width || 120 }}
                      className="h-auto"
                    />
                  ) : logoMarkUrl ? (
                    <>
                      <img src={logoMarkUrl} alt="" className="h-8 w-8" />
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {portalName}
                      </span>
                    </>
                  ) : (
                    <span className="font-semibold text-gray-900 dark:text-white text-lg">
                      {portalName}
                    </span>
                  )}
                </NavLink>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden p-2 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </>
            )}
          </div>

          {/* Navigation */}
          <nav className={clsx('flex-1 overflow-y-auto py-4', sidebarCollapsed ? 'px-2' : 'px-2')}>
            <div className={clsx('space-y-1')}>
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.exact}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center text-sm font-medium rounded-md transition-colors',
                      sidebarCollapsed ? 'justify-center p-2' : 'px-3 py-2',
                      isActive
                        ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    )
                  }
                  onClick={() => setSidebarOpen(false)}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <item.icon className={clsx('h-5 w-5', !sidebarCollapsed && 'mr-3')} />
                  {!sidebarCollapsed && item.label}
                </NavLink>
              ))}
            </div>
          </nav>

          {/* Build version info */}
          {!sidebarCollapsed && (
            <div className="px-3 py-2">
              <div className="text-xs text-gray-400 dark:text-gray-600 font-mono">
                {__BUILD_NUMBER__} &bull; {__COMMIT_SHA__.substring(0, 7)}
              </div>
            </div>
          )}

          {/* Bottom section */}
          <div className="border-t border-gray-200 dark:border-gray-700">
            {sidebarCollapsed ? (
              <div className="p-2 space-y-1">
                {themeOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setTheme(option.value)}
                      className={clsx(
                        'w-full flex items-center justify-center p-2 rounded-md transition-colors',
                        theme === option.value
                          ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300'
                          : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      )}
                      title={option.label}
                    >
                      <Icon className="h-5 w-5" />
                    </button>
                  );
                })}
                <div className="border-t border-gray-200 dark:border-gray-700 my-2" />
                <div
                  className="w-full flex items-center justify-center p-2"
                  title={customer?.display_name || customer?.email}
                >
                  <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                    <User className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
                <button
                  onClick={() => logout()}
                  className="w-full flex items-center justify-center p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  title="Sign out"
                >
                  <LogOut className="h-5 w-5" />
                </button>
                <div className="border-t border-gray-200 dark:border-gray-700 my-2" />
                <button
                  onClick={() => setSidebarCollapsed(false)}
                  className="hidden lg:flex w-full items-center justify-center p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  title="Expand sidebar"
                >
                  <PanelLeft className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center min-w-0 flex-1">
                    <div className="flex-shrink-0 h-9 w-9 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div className="ml-3 min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {customer?.display_name || customer?.email}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {customer?.company_name}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => logout()}
                    className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                    title="Sign out"
                  >
                    <LogOut className="h-5 w-5" />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    {themeOptions.map((option) => {
                      const Icon = option.icon;
                      return (
                        <button
                          key={option.value}
                          onClick={() => setTheme(option.value)}
                          className={clsx(
                            'p-1.5 rounded-md transition-colors',
                            theme === option.value
                              ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                          )}
                          title={option.label}
                        >
                          <Icon className="h-4 w-4" />
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setSidebarCollapsed(true)}
                    className="hidden lg:flex items-center p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                    title="Collapse sidebar"
                  >
                    <PanelLeftClose className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={clsx('transition-[padding] duration-200', mainPadding)}>
        {/* Mobile header */}
        <header className={clsx('lg:hidden sticky z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700', bannerShown ? 'top-7' : 'top-0')}>
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Menu className="h-6 w-6" />
            </button>
            {logoMarkUrl ? (
              <img src={logoMarkUrl} alt="" className="h-8 w-8" />
            ) : (
              <span className="font-semibold text-gray-900 dark:text-white">{portalName}</span>
            )}
            <div className="w-10" />
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
